/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, CSSProperties, ReactElement } from 'react';
import { cssMap } from '@compiled/react';

import { PanelType } from '@atlaskit/adf-schema/panel';
import { jsx } from '@atlaskit/css';
import {
	PanelSharedCssClassName,
	getPanelTypeBackground,
} from '@atlaskit/editor-common/styles/panel';
import type { Panel as OriginalPanel } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const styles = cssMap({
	panel: {
		alignItems: 'normal',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		boxSizing: 'border-box',
		color: 'inherit',
		display: 'flex',
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		marginTop: token('space.150'),
		minWidth: '48px',
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		position: 'relative',
		wordBreak: 'break-word',
	},
	withoutIcon: {
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
	},
	icon: {
		boxSizing: 'content-box',
		flexShrink: 0,
		height: token('space.300'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Match Platform panel icon geometry during light-to-full replacement.
		marginTop: '0.1em',
		paddingRight: token('space.100'),
		width: token('space.300'),
	},
	content: {
		flexBasis: 0,
		flexGrow: 1,
		flexShrink: 0,
		marginTop: token('space.025'),
		marginRight: token('space.0'),
		marginBottom: token('space.025'),
		marginLeft: token('space.0'),
		minWidth: 0,
	},
});

const getPanelStyle = (
	panelType: ComponentProps<typeof OriginalPanel>['panelType'],
	panelColor: ComponentProps<typeof OriginalPanel>['panelColor'],
): CSSProperties => ({
	backgroundColor:
		panelColor ??
		(panelType === 'custom'
			? token('color.background.neutral')
			: getPanelTypeBackground(panelType)),
});

export const Panel = (
	props: ComponentProps<typeof OriginalPanel> & LightNodeProps,
): ReactElement => {
	const panelType =
		props.panelType === PanelType.CUSTOM && !props.allowCustomPanels
			? PanelType.INFO
			: props.panelType;
	const hasIcon = panelType !== PanelType.CUSTOM || Boolean(props.panelIcon && props.providers);

	// Match the full panel's loadable wrapper so breakout first-child margin resets
	// apply to the wrapper in both modes, preserving the panel's own top margin.
	return (
		<span>
			<div
				ref={props.lightNodeRef}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's panel nesting and dense-mode style hooks.
				className={`${PanelSharedCssClassName.prefix}${hasIcon ? '' : ` ${PanelSharedCssClassName.noIcon}`}`}
				css={[styles.panel, !hasIcon && styles.withoutIcon]}
				data-local-id={props.localId}
				data-panel-color={props.panelColor}
				data-panel-icon={props.panelIcon}
				data-panel-icon-id={props.panelIconId}
				data-panel-icon-text={props.panelIconText}
				data-panel-type={panelType}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Panel background is selected from runtime ADF type and custom color values.
				style={getPanelStyle(panelType, props.panelColor)}
			>
				{hasIcon && (
					<div
						aria-hidden
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's panel icon layout hook.
						className={PanelSharedCssClassName.icon}
						css={styles.icon}
					/>
				)}
				<div
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's nested-content style hook.
					className={PanelSharedCssClassName.content}
					css={styles.content}
				>
					{props.children}
				</div>
			</div>
		</span>
	);
};
